import { createContext, useContext } from 'react';
import type { InjectedRouter } from 'react-router';
import type { SetNonNullable } from 'type-fest';

import type { RouterProps } from '../../../../../types';

import type useAuthenticationSettings from './useAuthenticationSettings';
import type useOrganizationDomains from './useOrganizationDomains';

// No need to forward isLoading, since we are handling the loading state before passing it to the context.
type AuthenticationSettingsValues = Omit<ReturnType<typeof useAuthenticationSettings>, 'isLoading'>;
type OrganizationDomainsValues = Omit<ReturnType<typeof useOrganizationDomains>, 'isLoading'>;
// Values are not nullable because we are handling the loading state before passing it to the context.
export type AuthenticationSettingsContextValue = SetNonNullable<AuthenticationSettingsValues, 'settings'> &
  SetNonNullable<OrganizationDomainsValues, 'domains'> & {
    location: RouterProps['location'];
    router: InjectedRouter;
  };

export const AuthenticationSettingsContext = createContext<AuthenticationSettingsContextValue | null>(null);

const useAuthenticationSettingsContext = () => {
  const context = useContext(AuthenticationSettingsContext);
  if (!context) {
    throw new Error('useAuthenticationSettingsContext must be used within an AuthenticationSettingsContextProvider');
  }
  return context;
};

export default useAuthenticationSettingsContext;
